import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import {
  REASON_OPTIONS,
  BEDROOM_OPTIONS,
  CONTACT_OPTIONS,
  CONTACT_TIME_OPTIONS,
  PROPERTY_TYPES,
  HOUSE_STYLES,
  BUNGALOW_STYLES,
  RELATIONSHIP_OPTIONS,
  createAndSendSchema,
  WHEN_OPTIONS,
  TENANT_REASON_OPTIONS,
} from '@propertypal/shared/src/constants/instantValuation';
import useLoqate, { useLoqateWithFormik } from '@propertypal/shared/src/hooks/useLoqate';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { Property } from '@propertypal/shared/src/types';
import { ValuationResult } from '@propertypal/shared/src/types/valuations';
import getAccountNumber from '@propertypal/shared/src/utils/property/getAccountNumber';
import getSellingAgent, { getAgentColours } from '@propertypal/shared/src/utils/property/getSellingAgent';
import { getCreateAndSendPayload } from '@propertypal/shared/src/utils/valuations';
import Button from '@propertypal/web-ui/src/buttons/Button';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import PhoneNumberInput from '@propertypal/web-ui/src/inputs/PhoneNumberInput';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import { Box } from '@propertypal/web-ui/src/layout';
import Recaptcha, { getCaptchaKey } from '@propertypal/web-ui/src/security/Recaptcha';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { SubHeading, Text } from '@propertypal/web-ui/src/typography';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import useCachedUser from '../../hooks/useCachedUser';
import {
  EnquiryFormColumn,
  EnquiryFormContainer,
  EnquiryFormInput,
  EnquiryFormButtonContainer,
} from './ValuationRequestForm.style';

interface EnquiryFormProps {
  onSuccess: (accountNumber: string) => void;
  property: Property;
  initialAccountNumber?: string;
}

const validationSchema = yup
  .object()
  .shape({
    accountNumber: yup.string().required('Please select a contact'),
  })
  .concat(createAndSendSchema);

const ValuationRequestForm: React.FC<EnquiryFormProps> = ({ property, onSuccess, initialAccountNumber }) => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { loqateId, value, handleValueChange, options, handleOptionClick } = useLoqate();
  const [manualEntry, setManualEntry] = useState(false);
  const dispatch = useDispatch();
  const sellingAgent = getSellingAgent(property);
  const { pdpBrandingButtonBgColour, pdpBrandingButtonTextColour, pdpBrandingLinkColour } = getAgentColours(
    property.enhancedBranding,
    sellingAgent,
  );

  const formik = useFormik({
    initialValues: {
      accountNumber: initialAccountNumber || getAccountNumber(property),
      mode: 'agent',
      loqateId: '',
      subBuilding: '',
      buildingName: '',
      buildingNumber: '',
      street: '',
      town: '',
      postcode: '',
      propertyType: '',
      propertyStyle: '',
      bedrooms: '',
      valuationReason: '',
      relationship: '',
      timeframe: '',
      contactPreference: '',
      contactTimePreference: 'NO_PREFERENCE',
      email: '',
      phonePrefix: '+44',
      phoneNumber: '',
      name: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const recaptcha = await getCaptchaKey('valuation');

        await axios<ValuationResult>(
          {
            method: 'POST',
            url: `${API_URL}/valuation/create-and-send/recapatcha`,
            data: {
              ...getCreateAndSendPayload(values),
              recaptcha,
            },
          },
          dispatch,
        );

        onSuccess(values.accountNumber);

        setSent(true);
      } catch (err: any) {
        setLoading(false);

        openToast({
          message: "Something went wrong. We couldn't send your enquiry. Please try again!",
          type: 'error',
          title: 'Error',
        });
      }
    },
  });

  useCachedUser(property.loggedInUser, (profile) => {
    formik.setValues({
      ...formik.values,
      email: profile.email,
      name: profile.name,
      phonePrefix: profile.prefix,
      phoneNumber: profile.phoneNumberNoPrefix,
    });
  });

  useLoqateWithFormik(formik, value, loqateId);

  if (sent)
    return (
      <Box
        data-testid="request-sent"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        m="40px auto"
      >
        <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 46 }} />
        <SubHeading textTransform="uppercase" mt={20} fontSize={28} fontWeight="600">
          Thanks!
        </SubHeading>
        <Text mt={20} align="center" fontSize={18}>
          Your valuation request has been sent successfully.
        </Text>
      </Box>
    );

  return (
    <>
      <Recaptcha />

      <SubHeading align="center" fontSize={20}>
        Thinking of selling or letting your property? Find out what your home is worth with a no-obligation valuation.
      </SubHeading>

      <form onSubmit={formik.handleSubmit} data-testid="request-valuation-form">
        <SubHeading fontSize={18} color={pdpBrandingLinkColour} mt={30} mb={13}>
          Address
        </SubHeading>

        {!manualEntry && (
          <EnquiryFormInput>
            <TextInput
              containerStyle={{ marginTop: 20 }}
              placeholder="Enter your address..."
              label="Address lookup"
              name="addressLookup"
              value={value}
              onValueChange={handleValueChange}
              autoCompleteOptions={options}
              onOptionClick={handleOptionClick}
              error={formik.touched.loqateId && !formik.values.loqateId && 'Please enter an address'}
              autoCompleteText="Enter address manually"
              onAutoCompleteTextClick={() => {
                setManualEntry(true);
                formik.setFieldValue('loqateId', '');
              }}
              testID="addressLookup"
            />
          </EnquiryFormInput>
        )}
        {manualEntry && (
          <>
            <EnquiryFormContainer>
              <EnquiryFormColumn>
                <TextInput
                  name="flat-number"
                  placeholder="Flat Number"
                  label="Flat number"
                  testID="flatNumberInput"
                  onValueChange={formik.handleChange('subBuilding')}
                  value={formik.values.subBuilding}
                  error={formik.touched.subBuilding && formik.errors.subBuilding}
                />
              </EnquiryFormColumn>
              <EnquiryFormColumn>
                <TextInput
                  name="building-name"
                  placeholder="Building Name"
                  label="Building name"
                  testID="buildingNameInput"
                  onValueChange={formik.handleChange('buildingName')}
                  value={formik.values.buildingName}
                  error={formik.touched.buildingName && formik.errors.buildingName}
                />
              </EnquiryFormColumn>
            </EnquiryFormContainer>

            <EnquiryFormContainer>
              <EnquiryFormColumn>
                <TextInput
                  data-testid="vr-house-number-enquiry"
                  name="buildingNumber"
                  placeholder="House Number"
                  label="House Number"
                  testID="buildingNumberInput"
                  onValueChange={formik.handleChange('buildingNumber')}
                  value={formik.values.buildingNumber}
                  error={formik.touched.buildingNumber && formik.errors.buildingNumber}
                />
              </EnquiryFormColumn>
              <EnquiryFormColumn>
                <TextInput
                  name="street"
                  placeholder="Street"
                  label="Street"
                  testID="streetInput"
                  onValueChange={formik.handleChange('street')}
                  value={formik.values.street}
                  error={formik.touched.street && formik.errors.street}
                />
              </EnquiryFormColumn>
            </EnquiryFormContainer>

            <EnquiryFormContainer>
              <EnquiryFormColumn>
                <TextInput
                  name="town"
                  placeholder="Town"
                  label="Town"
                  testID="townInput"
                  onValueChange={formik.handleChange('town')}
                  value={formik.values.town}
                  error={formik.touched.town && formik.errors.town}
                />
              </EnquiryFormColumn>
              <EnquiryFormColumn>
                <TextInput
                  name="postcode"
                  placeholder="Postcode"
                  label="Postcode"
                  testID="postcodeInput"
                  onValueChange={formik.handleChange('postcode')}
                  value={formik.values.postcode}
                  error={formik.touched.postcode && formik.errors.postcode}
                />
              </EnquiryFormColumn>
            </EnquiryFormContainer>
          </>
        )}
        <SubHeading fontSize={18} color={pdpBrandingLinkColour} mt={30} mb={13}>
          About the property
        </SubHeading>

        <EnquiryFormContainer>
          <EnquiryFormColumn>
            <EnquiryFormInput>
              <SelectInput
                id="propertyType"
                ariaLabel="Property Type"
                placeholder="Property type"
                label="Property type"
                options={PROPERTY_TYPES}
                testID="propertyTypeSelect"
                onChange={formik.handleChange('propertyType')}
                value={formik.values.propertyType}
                error={formik.touched.propertyType && formik.errors.propertyType}
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>

          <EnquiryFormColumn>
            <EnquiryFormInput>
              <SelectInput
                id="propertyStyle"
                ariaLabel="Property Style"
                placeholder="Property style"
                label="Property style"
                disabled={!['HOUSE', 'BUNGALOW'].includes(formik.values.propertyType)}
                options={formik.values.propertyType === 'HOUSE' ? HOUSE_STYLES : BUNGALOW_STYLES}
                testID="propertyStyleSelect"
                onChange={formik.handleChange('propertyStyle')}
                value={formik.values.propertyStyle}
                error={formik.touched.propertyStyle && formik.errors.propertyStyle}
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>
        </EnquiryFormContainer>

        <EnquiryFormContainer>
          <EnquiryFormColumn>
            <EnquiryFormInput>
              <SelectInput
                id="bedrooms"
                ariaLabel="Number of bedrooms"
                placeholder="Select number of bedrooms"
                label="Number of bedrooms"
                options={BEDROOM_OPTIONS}
                testID="bedroomsSelect"
                onChange={formik.handleChange('bedrooms')}
                value={formik.values.bedrooms}
                error={formik.touched.bedrooms && formik.errors.bedrooms}
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>
        </EnquiryFormContainer>

        <SubHeading fontSize={18} color={pdpBrandingLinkColour} mt={46} mb={13}>
          About the valuation
        </SubHeading>

        <EnquiryFormContainer>
          <EnquiryFormColumn>
            <EnquiryFormInput>
              <SelectInput
                id="relationship"
                ariaLabel="What is your relationship to this property?"
                placeholder="Select an option"
                label="What is your relationship to this property?"
                options={RELATIONSHIP_OPTIONS('agent')}
                testID="relationshipSelect"
                onChange={formik.handleChange('relationship')}
                value={formik.values.relationship}
                error={formik.touched.relationship && formik.errors.relationship}
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>

          <EnquiryFormColumn>
            <EnquiryFormInput>
              <SelectInput
                id="valuationReason"
                ariaLabel="Why would you like a valuation?"
                placeholder="Select reason"
                label="Why would you like a valuation?"
                options={formik.values.relationship === 'TENANT' ? TENANT_REASON_OPTIONS : REASON_OPTIONS}
                testID="valuationReasonSelect"
                onChange={formik.handleChange('valuationReason')}
                value={formik.values.valuationReason}
                error={formik.touched.valuationReason && formik.errors.valuationReason}
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>
        </EnquiryFormContainer>

        <EnquiryFormContainer>
          {['SELL', 'LET'].includes(formik.values.valuationReason) && formik.values.relationship !== 'TENANT' && (
            <EnquiryFormColumn>
              <EnquiryFormInput>
                <SelectInput
                  id="timeframe"
                  ariaLabel={`When would you like to ${formik.values.valuationReason.toLowerCase()} the property?`}
                  placeholder="Select reason"
                  label={`When would you like to ${formik.values.valuationReason.toLowerCase()} the property?`}
                  options={WHEN_OPTIONS}
                  testID="timeframeSelect"
                  onChange={formik.handleChange('timeframe')}
                  value={formik.values.timeframe}
                  error={formik.touched.timeframe && formik.errors.timeframe}
                />
              </EnquiryFormInput>
            </EnquiryFormColumn>
          )}
        </EnquiryFormContainer>

        <SubHeading fontSize={18} color={pdpBrandingLinkColour} mt={46} mb={13}>
          Your details
        </SubHeading>

        <EnquiryFormContainer>
          <EnquiryFormColumn>
            <EnquiryFormInput>
              <TextInput
                placeholder="Your name"
                label="Your name"
                value={formik.values.name}
                name="name"
                onValueChange={formik.handleChange('name')}
                testID="nameInput"
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>

          <EnquiryFormColumn>
            <EnquiryFormInput>
              <PhoneNumberInput
                placeholder="Your phone number"
                label="Your phone number"
                onValueChange={formik.handleChange('phoneNumber')}
                value={formik.values.phoneNumber}
                onPrefixChange={formik.handleChange('phonePrefix')}
                prefix={formik.values.phonePrefix}
                error={formik.touched.phoneNumber && formik.errors.phoneNumber}
                testID="phoneInput"
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>
        </EnquiryFormContainer>

        <EnquiryFormContainer>
          <EnquiryFormColumn>
            <EnquiryFormInput>
              <TextInput
                placeholder="Your email address"
                label="Your email address"
                value={formik.values.email}
                name="email"
                onValueChange={formik.handleChange('email')}
                testID="emailInput"
                error={formik.touched.email && formik.errors.email}
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>
          <EnquiryFormColumn>
            <EnquiryFormInput>
              <SelectInput
                id="contactPreference"
                ariaLabel="Your contact preference"
                placeholder="Select a contact preference"
                label="Your contact preference"
                options={CONTACT_OPTIONS}
                testID="contactPreferenceSelect"
                onChange={formik.handleChange('contactPreference')}
                value={formik.values.contactPreference}
                error={formik.touched.contactPreference && formik.errors.contactPreference}
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>
        </EnquiryFormContainer>

        <EnquiryFormContainer>
          <EnquiryFormColumn>
            <EnquiryFormInput>
              <SelectInput
                id="contactTimePreference"
                ariaLabel="Select contact time preference"
                placeholder="Select contact time preference"
                label="Select contact time preference"
                options={CONTACT_TIME_OPTIONS}
                testID="contactTimePreferenceSelect"
                onChange={formik.handleChange('contactTimePreference')}
                value={formik.values.contactTimePreference}
                error={formik.touched.contactTimePreference && formik.errors.contactTimePreference}
              />
            </EnquiryFormInput>
          </EnquiryFormColumn>
        </EnquiryFormContainer>

        <EnquiryFormButtonContainer>
          <Button
            color={pdpBrandingButtonBgColour}
            fontColor={pdpBrandingButtonTextColour}
            loading={loading}
            testID="submitValuationRequest"
            type="submit"
          >
            Request a Valuation
          </Button>
        </EnquiryFormButtonContainer>
      </form>
    </>
  );
};

export default ValuationRequestForm;
